import Transition, {
  TransitionVariant
} from '@aurora/shared-client/components/common/Transition/Transition';
import { KeyBoardEvents } from '@aurora/shared-types/community/enums';
import React, { useEffect, useRef, useState } from 'react';
import { tabbable } from 'tabbable';
import type { OverlayInjectedProps } from 'react-bootstrap/lib/esm/Overlay';
import type {
  OverlayTriggerProps,
  OverlayTriggerRenderProps
} from 'react-bootstrap/lib/esm/OverlayTrigger';
import { OverlayTrigger } from 'react-bootstrap';
import { hoverCardPopperConfig } from '../HoverCardHelper';

interface MouseHandlers {
  onMouseEnter: React.MouseEventHandler<HTMLElement>;
  onMouseLeave: React.MouseEventHandler<HTMLElement>;
}

interface KeyboardHandlers {
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
}

export type ExtendedOverlayInjectedProps = Omit<
  OverlayInjectedProps,
  'arrowProps' | 'placement' | 'popper' | 'show'
> &
  MouseHandlers;
export type ExtendedOverlayTriggerInjectedProps = OverlayTriggerRenderProps &
  MouseHandlers &
  KeyboardHandlers & { 'data-hovercard-trigger': string };

export interface Props
  extends Omit<
    OverlayTriggerProps,
    'show' | 'onToggle' | 'delay' | 'placement' | 'trigger' | 'overlay' | 'children'
  > {
  overlay: React.FC<React.PropsWithChildren<ExtendedOverlayInjectedProps>>;
  children: React.FC<React.PropsWithChildren<ExtendedOverlayTriggerInjectedProps>>;
}

/**
 * Wrapper around the react-bootstrap `OverlayTrigger` that adds support for the Overlay
 * to remain open when the mouse is inside the Overlay element, not just inside the trigger
 * element. To facilitate this functionality, the injected props for both the specified
 * Overlay and Trigger components are provided with callback functions for `onMouseEnter`
 * and `onMouseLeave` that must be used on the outermost native DOM elements of the
 * rendering components.
 *
 * This wrapper enforces that the overlay appears on hover and focus, with a pre-defined
 * delay for showing and hiding the overlay.
 *
 * @author Adam Ayres, Neha Anandpara
 */
const ExtendedOverlayTrigger: React.FC<Props> = props => {
  const { overlay, children, ...remainingProps } = props;
  const inPopoverRef = useRef(false);
  const inTriggerRef = useRef(false);
  const [show, setShow] = useState(false);
  const showDelay = 400;
  const hideDelay = 200;
  const triggerElementRef = useRef<HTMLElement>(null);
  const overlayElementRef = useRef<HTMLElement>(null);
  const suppressOpenOnNextTabRef = useRef(false);

  function handleToggle(updatedShow = false) {
    setShow(updatedShow || inPopoverRef.current || inTriggerRef.current);
  }

  function toggleRef(ref, state) {
    ref.current = state;
  }

  function handlePopoverMouseLeave() {
    toggleRef(inPopoverRef, false);
    setTimeout(() => {
      handleToggle();
    }, hideDelay);
  }

  const handleOverlayTab = React.useCallback((event: KeyboardEvent) => {
    if (event.key !== 'Tab' || !overlayElementRef.current || !triggerElementRef.current) {
      return;
    }

    const active = document.activeElement as HTMLElement;
    if (!overlayElementRef.current.contains(active)) {
      return;
    }

    const items = tabbable(overlayElementRef.current);
    const isFirst = items.length === 0 || active === items[0];
    const isLast = items.length === 0 || active === items.at(-1);

    if ((event.shiftKey && isFirst) || (!event.shiftKey && isLast)) {
      event.preventDefault();
      toggleRef(inPopoverRef, false);
      toggleRef(inTriggerRef, false);
      setTimeout(() => {
        handleToggle();
        if (event.shiftKey) {
          triggerElementRef.current?.focus();
        } else {
          const triggerElement = triggerElementRef.current;
          if (!triggerElement) {
            return;
          }
          const all = tabbable(document.body);
          const idx = all.indexOf(triggerElement as unknown as HTMLElement);
          if (idx > -1 && idx + 1 < all.length) {
            (all[idx + 1] as HTMLElement).focus();
          }
        }
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (!show) {
      return;
    }

    // Restore focus if element removed
    const observer = new MutationObserver(() => {
      if (document.activeElement === document.body) {
        setTimeout(() => {
          if (document.activeElement === document.body) {
            toggleRef(inPopoverRef, false);
            toggleRef(inTriggerRef, false);
            handleToggle();
            triggerElementRef.current?.focus();
          }
        }, 0);
      }
    });

    if (overlayElementRef.current) {
      observer.observe(overlayElementRef.current, { childList: true, subtree: true });
    }

    document.addEventListener('keydown', handleOverlayTab, true);

    return () => {
      document.removeEventListener('keydown', handleOverlayTab, true);
      observer.disconnect();
    };
  }, [show, handleOverlayTab]);

  function renderOverlay(popoverProps: OverlayInjectedProps) {
    return (
      <Transition variant={TransitionVariant.FADE_SLOW} in={show} appear unmountOnExit>
        {overlay({
          ...popoverProps,
          ref: (node: HTMLElement) => {
            overlayElementRef.current = node;
            (popoverProps.ref as ((n: HTMLElement) => void) | undefined)?.(node);
          },
          onMouseEnter: () => toggleRef(inPopoverRef, true),
          onMouseLeave: () => handlePopoverMouseLeave()
        })}
      </Transition>
    );
  }

  function renderTrigger(triggerProps: OverlayTriggerRenderProps) {
    return children({
      ...triggerProps,
      ref: (node: HTMLElement) => {
        triggerElementRef.current = node;
        (triggerProps.ref as ((n: HTMLElement) => void) | undefined)?.(node);
      },
      onMouseEnter: () => toggleRef(inTriggerRef, true),
      onMouseLeave: () => toggleRef(inTriggerRef, false),
      onKeyDown: event => {
        const { key } = event;
        if (key === KeyBoardEvents.ESCAPE) {
          toggleRef(inTriggerRef, false);
          handleToggle();
          suppressOpenOnNextTabRef.current = true;
          return;
        }
        if (key === 'Tab' && !event.shiftKey) {
          if (suppressOpenOnNextTabRef.current) {
            // Allow normal tabbing to next element and clear suppression
            suppressOpenOnNextTabRef.current = false;
            return;
          }
          event.preventDefault();
          toggleRef(inPopoverRef, true);
          setTimeout(() => {
            handleToggle();
            const overlayElement = overlayElementRef.current;
            if (!overlayElement) {
              return;
            }
            const tabbables = tabbable(overlayElement);
            if (tabbables.length > 0) {
              (tabbables[0] as HTMLElement).focus();
            }
          }, 0);
        }
      },
      'data-hovercard-trigger': 'true'
    });
  }

  return (
    <OverlayTrigger
      /* eslint-disable react/jsx-props-no-spreading */
      {...remainingProps}
      show={show}
      delay={{ show: showDelay, hide: hideDelay }}
      overlay={overlayProps => renderOverlay(overlayProps)}
      onToggle={updatedShow => handleToggle(updatedShow)}
      trigger={['focus', 'hover']}
      placement="auto"
      popperConfig={hoverCardPopperConfig()}
      transition={false}
    >
      {triggerProps => renderTrigger(triggerProps)}
    </OverlayTrigger>
  );
};

export default ExtendedOverlayTrigger;
